import { useRef, useState } from 'react';
import { Button } from '@/ui/Button';
import { Card } from '@/ui/Card';
import { ConfirmDialog } from '@/ui/Dialog';
import { Field, Input, Select, Textarea } from '@/ui/Field';
import { Skeleton } from '@/ui/Skeleton';
import { EstadoError } from '@/components/EstadoError';
import { cn } from '@/lib/cn';
import { CANALES } from './piezas';
import {
  useBuscarDestinatarios,
  useEnviar,
  useOpcionesDeAudiencia,
  usePrevisualizarAudiencia,
  type Canal,
  type ParametrosDeAudiencia,
  type TipoDeAudiencia,
} from './api';

/**
 * Compositor.tsx — opción 1 del módulo 6: escribir una comunicación.
 *
 * ## Lo que se arregla aquí
 *
 * 1. **La selección manual deja de pedir números de fila.** El vanilla ponía
 *    `<input placeholder="IDs separados por coma (ej: 3,5,8)" />`, es decir,
 *    había que saberse el id de cada persona en la base de datos. Ahora es un
 *    buscador por nombre contra un endpoint que ya existía.
 *
 * 2. **Los nombres de la muestra de destinatarios dejan de ir crudos al DOM.**
 *    `previewAudience` hacía `el.innerHTML = …${names}…` con hasta doce nombres
 *    completos concatenados. React escapa por construcción.
 *
 * 3. **Los nombres de los catálogos también.** `_renderAudienceParams`
 *    interpolaba `${o[label]}` —área, empresa, sede, cargo— sin escapar; se le
 *    escapaba a `test_escapado` porque el acceso es por variable (`o[label]`) y
 *    no por nombre de propiedad.
 *
 * 4. **La validación deja de ser un aviso que desaparece.** Antes, título y
 *    contenido vacíos daban un `toast` y nada más: el aviso se iba solo a los
 *    pocos segundos y no decía en qué campo estabas. Ahora el error vive en el
 *    campo y el foco va al primero que falta.
 *
 * ## Lo que NO cambia
 *
 * El cuerpo que se manda a `POST /communications/send` es el mismo, campo por
 * campo, incluido `audience_params` con sus valores en texto. Lo vigila
 * `test_comunicaciones_pantalla.py`.
 */

/** Qué catálogo y qué clave de parámetro le toca a cada tipo de audiencia.
 *
 *  Estaba escrito dos veces en el vanilla —una en `_renderAudienceParams` para
 *  pintar el `<select>` y otra en `_audienceSpec` para leerlo—, con el riesgo
 *  clásico: añadir un tipo y que solo una de las dos se entere. */
const PARAMETRO = {
  por_area: { catalogo: 'areas', clave: 'area_id', etiqueta: 'Área' },
  por_rol: { catalogo: 'roles', clave: 'role', etiqueta: 'Rol' },
  por_empresa: { catalogo: 'companies', clave: 'company_id', etiqueta: 'Empresa' },
  por_sede: { catalogo: 'sedes', clave: 'sede_id', etiqueta: 'Sede' },
  por_cargo: { catalogo: 'positions', clave: 'position_id', etiqueta: 'Cargo' },
  por_etiqueta: { catalogo: 'tags', clave: 'tag', etiqueta: 'Etiqueta' },
} as const;

type TipoConParametro = keyof typeof PARAMETRO;

const tieneParametro = (t: TipoDeAudiencia): t is TipoConParametro => t in PARAMETRO;

export function Compositor() {
  const { data: opciones, isLoading, isError, error, refetch } = useOpcionesDeAudiencia();

  const [titulo, setTitulo] = useState('');
  const [cuerpo, setCuerpo] = useState('');
  const [esHtml, setEsHtml] = useState(false);
  const [canales, setCanales] = useState<Canal[]>(['feed']);
  const [tipo, setTipo] = useState<TipoDeAudiencia>('todos_activos');
  const [parametro, setParametro] = useState('');
  const [elegidos, setElegidos] = useState<Map<number, string>>(new Map());
  const [busqueda, setBusqueda] = useState('');
  const [programar, setProgramar] = useState('');
  const [errores, setErrores] = useState<Record<string, string>>({});
  const [confirmando, setConfirmando] = useState(false);

  const previsualizar = usePrevisualizarAudiencia();
  const enviar = useEnviar();
  const refTitulo = useRef<HTMLInputElement>(null);
  const refCuerpo = useRef<HTMLTextAreaElement>(null);

  const esManual = tipo === 'seleccion_manual';
  const { data: encontrados, isFetching: buscando } = useBuscarDestinatarios(busqueda, esManual);

  if (isLoading) {
    return (
      <Card className="p-5">
        <div className="flex flex-col gap-4">
          <Skeleton className="h-6 w-48" />
          <Skeleton className="h-10 w-full" />
          <Skeleton className="h-28 w-full" />
          <Skeleton className="h-10 w-2/3" />
        </div>
      </Card>
    );
  }

  // El compositor sí necesita los catálogos para funcionar, así que aquí el
  // error es suyo y se queda dentro de su tarjeta. Lo que ya no pasa es que se
  // lleve por delante el historial, que no los necesita para nada.
  if (isError || !opciones) {
    return (
      <Card className="p-5">
        <EstadoError
          err={error}
          que="las opciones de audiencia"
          onReintentar={() => refetch()}
        />
      </Card>
    );
  }

  /** Los parámetros tal como los espera `resolve_audience`. */
  function parametrosDeAudiencia(): ParametrosDeAudiencia {
    if (esManual) return { user_ids: [...elegidos.keys()] };
    if (tieneParametro(tipo)) return { [PARAMETRO[tipo].clave]: parametro || null };
    return {};
  }

  function alternarCanal(c: Canal) {
    setCanales((previos) =>
      previos.includes(c) ? previos.filter((x) => x !== c) : [...previos, c],
    );
    setErrores((e) => ({ ...e, canales: '' }));
  }

  function alternarPersona(id: number, nombre: string) {
    setElegidos((previos) => {
      const copia = new Map(previos);
      if (copia.has(id)) copia.delete(id);
      else copia.set(id, nombre);
      return copia;
    });
    setErrores((e) => ({ ...e, audiencia: '' }));
  }

  /** Valida y devuelve si se puede enviar. El foco va al primer campo que
   *  falta: un formulario largo con el error tres pantallas más arriba es un
   *  error que nadie ve. */
  function validar(): boolean {
    const nuevos: Record<string, string> = {};
    if (!titulo.trim()) nuevos.titulo = 'El comunicado necesita un título.';
    if (!cuerpo.trim()) nuevos.cuerpo = 'Escribe el mensaje que va a recibir la gente.';
    if (!canales.length) nuevos.canales = 'Elige al menos un canal de salida.';
    if (esManual && !elegidos.size) {
      nuevos.audiencia = 'Busca y marca al menos una persona.';
    } else if (tieneParametro(tipo) && !parametro) {
      nuevos.audiencia = `Selecciona ${PARAMETRO[tipo].etiqueta.toLowerCase()}.`;
    }
    setErrores(nuevos);
    if (nuevos.titulo) refTitulo.current?.focus();
    else if (nuevos.cuerpo) refCuerpo.current?.focus();
    return !Object.keys(nuevos).length;
  }

  function pedirConfirmacion() {
    if (validar()) setConfirmando(true);
  }

  function confirmarEnvio() {
    enviar.mutate(
      {
        title: titulo.trim(),
        body: cuerpo,
        is_html: esHtml,
        channels: canales,
        audience_type: tipo,
        audience_params: parametrosDeAudiencia(),
        schedule_at: programar ? new Date(programar).toISOString() : null,
      },
      {
        onSuccess: (r) => {
          setConfirmando(false);
          Utils.toast(
            r.status === 'programada' ? 'Comunicación programada' : 'Comunicación en envío',
            'success',
          );
          // Se limpia lo que identifica a ESTE comunicado. La audiencia y los
          // canales se conservan: quien manda tres avisos seguidos al mismo
          // grupo no debería reconstruir la segmentación cada vez.
          setTitulo('');
          setCuerpo('');
          setProgramar('');
          refTitulo.current?.focus();
        },
        onError: (e: unknown) => {
          setConfirmando(false);
          Utils.toast(e instanceof Error ? e.message : 'No se pudo enviar', 'error');
        },
      },
    );
  }

  const muestra = previsualizar.data;
  const nombresDeCanales = CANALES.filter((c) => canales.includes(c.clave))
    .map((c) => c.etiqueta)
    .join(', ');

  return (
    <Card className="p-5">
      <h2 className="mb-4 text-4 font-bold text-brand-ink">Nueva comunicación</h2>

      <div className="flex flex-col gap-4">
        <Field label="Título" error={errores.titulo}>
          <Input
            ref={refTitulo}
            value={titulo}
            onChange={(e) => {
              setTitulo(e.target.value);
              if (errores.titulo) setErrores((x) => ({ ...x, titulo: '' }));
            }}
            placeholder="Asunto del comunicado"
          />
        </Field>

        <Field
          label="Contenido"
          error={errores.cuerpo}
          ayuda={esHtml ? 'Se enviará tal cual, sin escapar: usa solo HTML de confianza.' : undefined}
        >
          <Textarea
            ref={refCuerpo}
            rows={6}
            value={cuerpo}
            onChange={(e) => {
              setCuerpo(e.target.value);
              if (errores.cuerpo) setErrores((x) => ({ ...x, cuerpo: '' }));
            }}
            placeholder="Escribe el mensaje…"
          />
        </Field>

        <label className="flex min-h-11 items-center gap-2 text-2 text-muted">
          <input
            type="checkbox"
            checked={esHtml}
            onChange={(e) => setEsHtml(e.target.checked)}
            className="size-4"
          />
          El contenido es HTML
        </label>

        {/* ── Canales ─────────────────────────────────────────────────── */}
        {/* `border-0 p-0 m-0`: el navegador dibuja un recuadro alrededor de todo
            `<fieldset>` y ningún otro campo del formulario lleva caja. Se usa
            fieldset igualmente porque agrupa tres casillas bajo una sola
            etiqueta, y eso es lo que hace que un lector de pantalla las lea
            como un grupo y no como tres casillas sueltas. */}
        <fieldset className="m-0 border-0 p-0">
          <legend className="mb-1.5 p-0 text-3 font-semibold text-ink">Canales</legend>
          <div className="flex flex-wrap gap-x-5 gap-y-1">
            {CANALES.map((c) => (
              <label
                key={c.clave}
                className="flex min-h-11 items-center gap-2 text-3"
                title={c.ayuda}
              >
                <input
                  type="checkbox"
                  checked={canales.includes(c.clave)}
                  onChange={() => alternarCanal(c.clave)}
                  className="size-4"
                />
                <i className={cn('bi', c.icono)} aria-hidden="true" />
                {c.etiqueta}
              </label>
            ))}
          </div>
          {errores.canales ? (
            <p role="alert" className="text-2 font-medium text-danger-ink">
              {errores.canales}
            </p>
          ) : (
            <p className="text-2 text-muted">
              {canales.length ? `Saldrá por: ${nombresDeCanales}.` : 'Sin canal no sale a ninguna parte.'}
            </p>
          )}
        </fieldset>

        {/* ── Audiencia ───────────────────────────────────────────────── */}
        <div className="flex flex-col gap-3 sm:flex-row sm:items-start">
          <Field label="Audiencia" className="sm:flex-1">
            <Select
              value={tipo}
              onChange={(e) => {
                setTipo(e.target.value as TipoDeAudiencia);
                setParametro('');
                setElegidos(new Map());
                setErrores((x) => ({ ...x, audiencia: '' }));
                previsualizar.reset();
              }}
            >
              {opciones.types.map((t) => (
                <option key={t.key} value={t.key}>
                  {t.label}
                </option>
              ))}
            </Select>
          </Field>

          {tieneParametro(tipo) && (
            <Field
              label={PARAMETRO[tipo].etiqueta}
              error={errores.audiencia}
              className="sm:flex-1"
            >
              <Select
                value={parametro}
                onChange={(e) => {
                  setParametro(e.target.value);
                  setErrores((x) => ({ ...x, audiencia: '' }));
                  previsualizar.reset();
                }}
              >
                <option value="">— Seleccionar —</option>
                {tipo === 'por_etiqueta'
                  ? opciones.tags.map((t) => (
                      <option key={t} value={t}>
                        {t}
                      </option>
                    ))
                  : tipo === 'por_rol'
                    ? opciones.roles.map((r) => (
                        <option key={r.key} value={r.key}>
                          {r.name}
                        </option>
                      ))
                    : opciones[PARAMETRO[tipo].catalogo as 'areas'].map((o) => (
                        <option key={o.id} value={String(o.id)}>
                          {o.name}
                        </option>
                      ))}
              </Select>
            </Field>
          )}
        </div>

        {/* ── Selección manual: un buscador, no una lista de ids ──────── */}
        {esManual && (
          <div className="flex flex-col gap-2">
            <Field label="Buscar colaborador" error={errores.audiencia}>
              <Input
                type="search"
                value={busqueda}
                onChange={(e) => setBusqueda(e.target.value)}
                placeholder="Nombre de la persona…"
                aria-describedby="ayuda-manual"
              />
            </Field>
            <p id="ayuda-manual" className="sr-only">
              Marca las personas que van a recibir la comunicación.
            </p>

            <div className="max-h-48 overflow-auto rounded-sm border border-line">
              {buscando && !encontrados ? (
                <p className="p-3 text-2 text-muted">Buscando…</p>
              ) : !encontrados?.length ? (
                <p className="p-3 text-2 text-muted">Sin resultados.</p>
              ) : (
                <ul>
                  {encontrados.map((p) => (
                    <li key={p.id}>
                      <label className="flex min-h-11 cursor-pointer items-center gap-2 px-3 py-1.5 text-3 hover:bg-sunken">
                        <input
                          type="checkbox"
                          checked={elegidos.has(p.id)}
                          onChange={() => alternarPersona(p.id, p.full_name)}
                          className="size-4"
                        />
                        <span className="min-w-0 flex-1 truncate">
                          {p.full_name}
                          {p.area && <span className="ml-1.5 text-muted">· {p.area}</span>}
                        </span>
                      </label>
                    </li>
                  ))}
                </ul>
              )}
            </div>

            {elegidos.size > 0 && (
              <div className="flex flex-wrap items-center gap-1.5">
                <span className="text-2 text-muted">
                  {elegidos.size} seleccionada{elegidos.size === 1 ? '' : 's'}:
                </span>
                {[...elegidos].map(([id, nombre]) => (
                  <button
                    key={id}
                    type="button"
                    onClick={() => alternarPersona(id, nombre)}
                    className="inline-flex min-h-8 items-center gap-1.5 rounded-xs bg-brand px-2 py-0.5
                               text-1 font-semibold text-on-brand focus-visible:focus-anillo
                               hover:bg-brand-strong"
                  >
                    {nombre}
                    <i className="bi bi-x-lg" aria-hidden="true" />
                    <span className="sr-only">Quitar de la lista</span>
                  </button>
                ))}
              </div>
            )}
          </div>
        )}

        {/* ── Previsualizar destinatarios ─────────────────────────────── */}
        <div className="flex flex-wrap items-center gap-3">
          <Button
            variant="secondary"
            size="sm"
            loading={previsualizar.isPending}
            onClick={() => previsualizar.mutate({
              audience_type: tipo,
              audience_params: parametrosDeAudiencia(),
            })}
          >
            <i className="bi bi-people" aria-hidden="true" /> Ver destinatarios
          </Button>

          {/* `aria-live`: quien usa lector de pantalla se entera del recuento
              sin tener que ir a buscarlo. */}
          <p className="text-2 text-muted" aria-live="polite">
            {previsualizar.isError
              ? (previsualizar.error instanceof Error ? previsualizar.error.message : 'No se pudo calcular')
              : muestra
                ? (
                  <>
                    <b className="text-brand-ink">{muestra.count}</b>{' '}
                    destinatario{muestra.count === 1 ? '' : 's'}
                    {muestra.sample.length > 0 && (
                      <>
                        {': '}
                        {muestra.sample.map((s) => s.full_name).join(', ')}
                        {muestra.count > muestra.sample.length &&
                          ` y ${muestra.count - muestra.sample.length} más`}
                      </>
                    )}
                  </>
                )
                : ''}
          </p>
        </div>

        <Field
          label="Programar envío"
          opcional
          ayuda="Si lo dejas vacío, sale en cuanto confirmes."
        >
          <Input
            type="datetime-local"
            value={programar}
            onChange={(e) => setProgramar(e.target.value)}
            className="max-w-xs"
          />
        </Field>

        <div className="flex justify-end">
          <Button onClick={pedirConfirmacion} loading={enviar.isPending}>
            <i className="bi bi-send" aria-hidden="true" /> Enviar
          </Button>
        </div>
      </div>

      <ConfirmDialog
        abierto={confirmando}
        onCambio={setConfirmando}
        titulo={programar ? 'Programar comunicación' : 'Enviar comunicación'}
        // Una confirmación tiene que decir QUÉ va a pasar. «¿Estás seguro?» no
        // aporta un dato que quien pulsa no tuviera ya.
        descripcion={
          `«${titulo.trim()}» saldrá por ${nombresDeCanales}` +
          (muestra ? ` a ${muestra.count} destinatario${muestra.count === 1 ? '' : 's'}` : '') +
          (programar
            ? ` el ${Utils.fmtDatetime(new Date(programar).toISOString())}.`
            : ' ahora mismo.') +
          (canales.includes('email') ? ' El correo no se puede retirar una vez enviado.' : '')
        }
        textoConfirmar={programar ? 'Programar' : 'Enviar ahora'}
        cargando={enviar.isPending}
        onConfirmar={confirmarEnvio}
      />
    </Card>
  );
}
